import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function ReviewPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, valid: false });
  const [form, setForm] = useState({
    quote: "",
    attribution: "",
    duration: "",
    consent: true,
  });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.publicReviewToken(token)
      .then((r) => setState({ loading: false, ...r }))
      .catch(() => setState({ loading: false, valid: false, reason: "error" }));
  }, [token]);

  useEffect(() => {
    if (state.expected_duration && !form.duration) {
      setForm((f) => ({ ...f, duration: state.expected_duration }));
    }
  }, [state.expected_duration]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.quote.length < 20) {
      toast.error("Please write at least 20 characters");
      return;
    }
    if (!form.attribution.trim()) {
      toast.error("Please add a nickname");
      return;
    }
    if (!form.consent) {
      toast.error("Consent is required");
      return;
    }
    setBusy(true);
    try {
      await api.publicReviewSubmit(token, form);
      setDone(true);
    } catch (err) {
      toast.error(err.message || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-ivory-dim">
        Loading…
      </div>
    );
  }

  if (!state.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="review-invalid">
        <div className="max-w-md text-center">
          <div className="eyebrow justify-center mx-auto mb-4">Review link</div>
          <h1 className="font-display text-4xl md:text-5xl text-gold mb-4">
            This link has already been used or expired.
          </h1>
          <p className="text-ivory-dim mb-6">
            If you'd like to leave feedback, please message Camilla directly and
            she'll send you a new link.
          </p>
          <Link to="/" className="btn-ghost-gold" data-testid="review-back-home">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="review-done">
        <div className="max-w-md text-center">
          <div className="eyebrow justify-center mx-auto mb-4">Thank you</div>
          <h1 className="font-display text-4xl md:text-5xl text-gold mb-4">
            Thank you love 🖤
          </h1>
          <p className="text-ivory-dim mb-6">
            Your review is in the queue. See you soon 💋
          </p>
          <Link to="/" className="btn-gold" data-testid="review-done-home">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16 px-5 md:px-8" data-testid="review-page">
      <div className="max-w-xl mx-auto">
        <div className="eyebrow mb-4">Leave a review</div>
        <h1 className="font-display text-4xl md:text-5xl text-gold leading-tight mb-3">
          Thanks for spending time with me 💋
        </h1>
        <p className="text-ivory-dim mb-8">
          Share what you'd want other guys to know. All reviews are shown
          anonymously — just a first-name or nickname.
        </p>

        <form onSubmit={submit} className="card-luxe p-6 space-y-4" data-testid="review-form">
          <label className="block">
            <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[color:var(--muted)]">
              Your review (20–500 chars)
            </span>
            <textarea
              rows={5}
              maxLength={500}
              className="input-luxe resize-y mt-2"
              value={form.quote}
              onChange={(e) => setForm({ ...form, quote: e.target.value })}
              placeholder="What was the vibe? What made it special?"
              data-testid="review-quote"
            />
            <div className="text-xs text-[color:var(--muted)] mt-1">
              {form.quote.length} / 500
            </div>
          </label>
          <label className="block">
            <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[color:var(--muted)]">
              Nickname / initial (shown publicly)
            </span>
            <input
              maxLength={40}
              className="input-luxe mt-2"
              value={form.attribution}
              onChange={(e) => setForm({ ...form, attribution: e.target.value })}
              placeholder="J from CBD"
              data-testid="review-attribution"
            />
          </label>
          <label className="block">
            <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[color:var(--muted)]">
              Booking duration
            </span>
            <input
              className="input-luxe mt-2"
              value={form.duration}
              onChange={(e) => setForm({ ...form, duration: e.target.value })}
              placeholder="1 hour"
              data-testid="review-duration"
            />
          </label>

          <div className="flex items-center gap-1 text-gold text-2xl select-none" aria-label="5 stars">
            {[0,1,2,3,4].map(i => <span key={i}>★</span>)}
          </div>

          <label className="flex items-start gap-2 text-sm text-ivory-dim cursor-pointer">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              className="mt-1 accent-[color:var(--gold)]"
              data-testid="review-consent"
            />
            <span>
              I confirm this review is genuine and I consent to it being shown
              publicly and anonymously.
            </span>
          </label>

          <button
            type="submit"
            className="btn-gold w-full"
            disabled={busy}
            data-testid="review-submit"
          >
            {busy ? "Submitting…" : "Submit review"}
          </button>
        </form>
      </div>
    </div>
  );
}
